import { safelyParseJSON } from "@excalidraw/common";
import { TTDStreamFetch } from "@excalidraw/excalidraw";

import type { LLMMessage } from "@excalidraw/excalidraw/components/TTDDialog/types";
import type {
  OpenAIInput,
  OpenAIOutput,
} from "@excalidraw/excalidraw/data/ai/types";

type AIBackendMode = "custom" | "localai";

export interface AIBackendConfig {
  mode: AIBackendMode;
  baseUrl: string;
  localAiModel: string;
  plusLp: string;
}

export interface TextToDiagramRequest {
  url: string;
  protocol: "custom" | "openai";
  requestBody: unknown;
}

export interface DiagramToCodeRequest {
  url: string;
  protocol: "custom" | "openai";
  requestBody: unknown;
}

export const DEFAULT_LOCALAI_MODEL = "llama3.1";

export const LOCALAI_TEXT_TO_DIAGRAM_PROMPT =
  "Return only valid Mermaid syntax. No markdown, no explanation.";

export const LOCALAI_DIAGRAM_TO_CODE_PROMPT =
  "Return only a complete HTML document. No markdown, no commentary.";

const DEFAULT_LOCALAI_TEMPERATURE = 0.2;

const normalizeBaseUrl = (value: string) =>
  String(value ?? "").replace(/\/+$/, "");

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const buildTextToDiagramSystemMessage =
  (): OpenAIInput.ChatCompletionMessageParam => {
    return {
      role: "system",
      content: LOCALAI_TEXT_TO_DIAGRAM_PROMPT,
    };
  };

const buildDiagramToCodeSystemMessage =
  (): OpenAIInput.ChatCompletionMessageParam => {
    return {
      role: "system",
      content: LOCALAI_DIAGRAM_TO_CODE_PROMPT,
    };
  };

const buildOpenAIChatMessages = (
  messages: readonly LLMMessage[],
): OpenAIInput.ChatCompletionMessageParam[] => {
  return messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
};

const buildRateLimitFallbackHtml = (plusLp: string) => {
  const supportLink = normalizeBaseUrl(plusLp);

  return `<html>
<body style="margin: 0; text-align: center">
<div style="display: flex; align-items: center; justify-content: center; flex-direction: column; height: 100vh; padding: 0 60px">
  <div style="color:red">Too many requests today,</br>please try again tomorrow!</div>
  </br>
  </br>
  <div>You can also try <a href="${supportLink}/plus?utm_source=onepaper&utm_medium=app&utm_content=d2c" target="_blank" rel="noopener">OnePaper+</a> to get more requests.</div>
</div>
</body>
</html>`;
};

const getErrorMessageFromResponse = async (response: Response) => {
  const text = await response.text();
  const parsed = safelyParseJSON(text);

  if (typeof parsed === "object" && parsed) {
    if (typeof (parsed as { message?: unknown }).message === "string") {
      return (parsed as { message: string }).message;
    }

    const error = (parsed as { error?: unknown }).error;
    if (isPlainObject(error) && typeof error.message === "string") {
      return error.message;
    }
  }

  return text || "Generation failed...";
};

const extractOpenAICompletionContent = (payload: unknown): string | null => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const choices = payload.choices;
  if (!Array.isArray(choices) || choices.length === 0) {
    return null;
  }

  const firstChoice = choices[0];
  if (!isPlainObject(firstChoice)) {
    return null;
  }

  const message = firstChoice.message;
  if (!isPlainObject(message)) {
    return null;
  }

  return typeof message.content === "string" ? message.content : null;
};

export const getAiBackendConfig = (
  env: Pick<
    ImportMetaEnv,
    | "VITE_APP_AI_BACKEND"
    | "VITE_APP_AI_BACKEND_MODE"
    | "VITE_APP_LOCALAI_BASE_URL"
    | "VITE_APP_LOCALAI_MODEL"
    | "VITE_APP_PLUS_LP"
  > = import.meta.env,
): AIBackendConfig => {
  const mode: AIBackendMode =
    env.VITE_APP_AI_BACKEND_MODE === "localai" ? "localai" : "custom";
  const baseUrl =
    mode === "localai"
      ? normalizeBaseUrl(
          env.VITE_APP_LOCALAI_BASE_URL || env.VITE_APP_AI_BACKEND,
        )
      : normalizeBaseUrl(env.VITE_APP_AI_BACKEND);

  return {
    mode,
    baseUrl,
    localAiModel: env.VITE_APP_LOCALAI_MODEL || DEFAULT_LOCALAI_MODEL,
    plusLp: normalizeBaseUrl(env.VITE_APP_PLUS_LP || ""),
  };
};

export const buildTextToDiagramRequest = ({
  messages,
  env,
}: {
  messages: readonly LLMMessage[];
  env?: Pick<
    ImportMetaEnv,
    | "VITE_APP_AI_BACKEND"
    | "VITE_APP_AI_BACKEND_MODE"
    | "VITE_APP_LOCALAI_BASE_URL"
    | "VITE_APP_LOCALAI_MODEL"
    | "VITE_APP_PLUS_LP"
  >;
}): TextToDiagramRequest => {
  const config = getAiBackendConfig(env ?? import.meta.env);

  if (config.mode === "localai") {
    return {
      url: `${config.baseUrl}/v1/chat/completions`,
      protocol: "openai",
      requestBody: {
        model: config.localAiModel,
        stream: true,
        temperature: DEFAULT_LOCALAI_TEMPERATURE,
        messages: [
          buildTextToDiagramSystemMessage(),
          ...buildOpenAIChatMessages(messages),
        ],
      },
    };
  }

  return {
    url: `${config.baseUrl}/v1/ai/text-to-diagram/chat-streaming`,
    protocol: "custom",
    requestBody: {
      messages,
    },
  };
};

export const buildDiagramToCodeRequest = ({
  texts,
  image,
  theme,
  env,
}: {
  texts: string;
  image: string;
  theme: string;
  env?: Pick<
    ImportMetaEnv,
    | "VITE_APP_AI_BACKEND"
    | "VITE_APP_AI_BACKEND_MODE"
    | "VITE_APP_LOCALAI_BASE_URL"
    | "VITE_APP_LOCALAI_MODEL"
    | "VITE_APP_PLUS_LP"
  >;
}): DiagramToCodeRequest => {
  const config = getAiBackendConfig(env ?? import.meta.env);

  if (config.mode === "localai") {
    return {
      url: `${config.baseUrl}/v1/chat/completions`,
      protocol: "openai",
      requestBody: {
        model: config.localAiModel,
        stream: false,
        temperature: DEFAULT_LOCALAI_TEMPERATURE,
        messages: [
          buildDiagramToCodeSystemMessage(),
          {
            role: "user",
            content: [
              {
                type: "text",
                text: `Texts:\n${texts}`,
              },
              {
                type: "text",
                text: `Theme:\n${theme}\nReturn a complete HTML document only.`,
              },
              {
                type: "image_url",
                image_url: {
                  url: image,
                  detail: "high",
                },
              },
            ],
          },
        ],
      },
    };
  }

  return {
    url: `${config.baseUrl}/v1/ai/diagram-to-code/generate`,
    protocol: "custom",
    requestBody: {
      texts,
      image,
      theme,
    },
  };
};

export const requestTextToDiagram = async ({
  messages,
  onChunk,
  onStreamCreated,
  signal,
  env,
}: {
  messages: readonly LLMMessage[];
  onChunk?: (chunk: string) => void;
  onStreamCreated?: () => void;
  signal?: AbortSignal;
  env?: Pick<
    ImportMetaEnv,
    | "VITE_APP_AI_BACKEND"
    | "VITE_APP_AI_BACKEND_MODE"
    | "VITE_APP_LOCALAI_BASE_URL"
    | "VITE_APP_LOCALAI_MODEL"
    | "VITE_APP_PLUS_LP"
  >;
}) => {
  const request = buildTextToDiagramRequest({ messages, env });

  return TTDStreamFetch({
    url: request.url,
    messages,
    onChunk,
    onStreamCreated,
    signal,
    protocol: request.protocol,
    requestBody: request.requestBody,
  });
};

export const requestDiagramToCode = async ({
  texts,
  image,
  theme,
  signal,
  env,
}: {
  texts: string;
  image: string;
  theme: string;
  signal?: AbortSignal;
  env?: Pick<
    ImportMetaEnv,
    | "VITE_APP_AI_BACKEND"
    | "VITE_APP_AI_BACKEND_MODE"
    | "VITE_APP_LOCALAI_BASE_URL"
    | "VITE_APP_LOCALAI_MODEL"
    | "VITE_APP_PLUS_LP"
  >;
}) => {
  const request = buildDiagramToCodeRequest({ texts, image, theme, env });
  const config = getAiBackendConfig(env ?? import.meta.env);

  const response = await fetch(request.url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request.requestBody),
    signal,
  });

  if (!response.ok) {
    if (response.status === 429) {
      return {
        html: buildRateLimitFallbackHtml(config.plusLp),
      };
    }

    throw new Error(await getErrorMessageFromResponse(response));
  }

  if (request.protocol === "openai") {
    try {
      const payload: OpenAIOutput.ChatCompletion = await response.json();
      const html = extractOpenAICompletionContent(payload);

      if (!html) {
        throw new Error("Generation failed (invalid response)");
      }

      return {
        html,
      };
    } catch {
      throw new Error("Generation failed (invalid response)");
    }
  }

  try {
    const { html } = await response.json();

    if (!html) {
      throw new Error("Generation failed (invalid response)");
    }

    return {
      html,
    };
  } catch {
    throw new Error("Generation failed (invalid response)");
  }
};
